import { useEffect, useState } from 'react'

/**
 * Named narrow-desktop tiers.
 *
 * CannaSpy is a dense operational tool used on real desktops, so the
 * interesting range is 900–1500, NOT phone widths. Before this hook the app
 * had exactly two media queries in the entire frontend and one hand-rolled
 * `window.innerWidth < 900` check, so every screen between 900 and 1400 got
 * the full wide-desktop layout and simply squeezed.
 *
 *   wide     >= 1400   everything docked, nothing competes for width
 *   desktop  1200–1399 docked but tightened
 *   compact  1024–1199 side panels undock; the table owns the width
 *   narrow   900–1023  toolbars wrap freely; sticky columns released
 *   stack    <  900    single column
 *
 * Tiers are ordered, so `atMost('compact')` / `atLeast('desktop')` read
 * naturally at the call site instead of scattering raw pixel numbers.
 */
export const BREAKPOINTS = {
  stack: 0,
  narrow: 900,
  compact: 1024,
  desktop: 1200,
  wide: 1400,
} as const

export type Tier = keyof typeof BREAKPOINTS

const ORDER: Tier[] = ['stack', 'narrow', 'compact', 'desktop', 'wide']

function tierFor(width: number): Tier {
  if (width >= BREAKPOINTS.wide) return 'wide'
  if (width >= BREAKPOINTS.desktop) return 'desktop'
  if (width >= BREAKPOINTS.compact) return 'compact'
  if (width >= BREAKPOINTS.narrow) return 'narrow'
  return 'stack'
}

export interface BreakpointState {
  tier: Tier
  width: number
  /** True when the viewport is at or above `t` — e.g. atLeast('desktop'). */
  atLeast: (t: Tier) => boolean
  /** True when the viewport is at or below `t`. */
  atMost: (t: Tier) => boolean
}

/**
 * Tracks the viewport tier. Listens on `resize` rather than five separate
 * matchMedia queries: one listener, one state update, and the numeric width
 * is available to callers that need to size something continuously (the
 * matrix column fitter does).
 */
export function useBreakpoint(): BreakpointState {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? BREAKPOINTS.wide : window.innerWidth,
  )

  useEffect(() => {
    let frame = 0
    const onResize = () => {
      // Coalesce to one update per frame — a drag-resize fires resize far
      // faster than React can reconcile a 2,000-row table.
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => setWidth(window.innerWidth))
    }
    window.addEventListener('resize', onResize)
    onResize()
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const tier = tierFor(width)
  const idx = ORDER.indexOf(tier)
  return {
    tier,
    width,
    atLeast: (t: Tier) => idx >= ORDER.indexOf(t),
    atMost: (t: Tier) => idx <= ORDER.indexOf(t),
  }
}
